import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../shared/listView/ListTable';
import { preferencesStorageInstance as preferencesStorage } from '../../services/preferencesStorage';
import Checkbox from '../Checkbox';
import PreferencesListItemGroups from './PreferencesListItemGroups';
import PreferencesListEditPreference from './PreferencesListEditPreference';
import { createPreferenceItemCodesValue, findFrequencyTitle } from './util';
import useWindowDimensions from '../../hooks/useWindowDimension';
import { TABLET_WIDTH } from '../../constant';
import PreferencesListSelectBlock from './PreferencesListSelectBlock';
import PreferencesListSelectAssignGroup from './PreferencesListSelectAssignGroup';
import { Preference } from '../../services/preferenceTypes';
import { Button } from '@chapche/ui';

type PreferenceItemsTableProps = {
    items: Preference[];
};

const PreferenceCheckbox = ({ preferenceId }: { preferenceId: string }) => {
    const [isChecked, setIsChecked] = useState(
        () => preferencesStorage.getSelectedList().some((it) => it.id === preferenceId),
    );

    useEffect(() => {
        const sync = () => {
            setIsChecked(preferencesStorage.getSelectedList().some((it) => it.id === preferenceId));
        };
        preferencesStorage.registerFiltersChangingObserver(sync);
        return () => preferencesStorage.unRegisterFiltersChangingObserver(sync);
    }, [preferenceId]);

    return (
        <Checkbox
            checked={isChecked}
            handleChecked={(checked) => preferencesStorage.selectPreference(preferenceId, checked)}
        />
    );
};

const PreferencePhrasesCount = ({ preference }: { preference: Preference }) => {
    const [phrasesCount, setPhrasesCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        preferencesStorage.getPreferencePhrasesCount(preference.id, (response) => setPhrasesCount(response.count));
    }, [preference.id]);

    return (
        <Button
            type="button"
            variant="ghost"
            size="small"
            className="list-table__link-button h-auto px-0 py-0 underline"
            onClick={() => navigate(preferencesStorage.getPathForPhrasesPage(preference))}
        >
            {phrasesCount}
        </Button>
    );
};

const PreferenceItemsTable = ({ items }: PreferenceItemsTableProps) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const [, setRevision] = useState(0);

    useEffect(() => {
        const onChange = () => setRevision((value) => value + 1);
        preferencesStorage.registerFiltersChangingObserver(onChange);
        return () => preferencesStorage.unRegisterFiltersChangingObserver(onChange);
    }, []);

    const columns = useMemo<ListTableColumn<Preference>[]>(() => [
        {
            id: 'check',
            header: '',
            className: 'list-table__td_check',
            cell: (row) => (
                <ListTableActions>
                    <PreferenceCheckbox preferenceId={row.id} />
                </ListTableActions>
            ),
        },
        {
            id: 'include',
            header: i18n.t('app.page.preference.addTitles.include'),
            cell: (row) => i18n.t(`preference.scheme.include.${row.include}`),
        },
        {
            id: 'type',
            header: i18n.t('app.page.preference.addTitles.preferenceType'),
            cell: (row) => i18n.t(`preference.scheme.preferenceType.${row.preferenceType}`),
        },
        {
            id: 'codes',
            header: i18n.t('app.page.preference.addTitles.preferenceItemCode'),
            cell: (row) => createPreferenceItemCodesValue(row, i18n),
        },
        {
            id: 'when',
            header: i18n.t('app.page.preference.addTitles.when'),
            hideBelow: TABLET_WIDTH + 1,
            cell: (row) => i18n.t(`preference.scheme.when.${row.when}`),
        },
        {
            id: 'howOften',
            header: i18n.t('app.page.preference.addTitles.howOften'),
            hideBelow: TABLET_WIDTH + 1,
            cell: (row) => findFrequencyTitle(row.preferenceFrequency, +row.frequencyPeriod, i18n),
        },
        {
            id: 'groups',
            header: i18n.t('app.page.group.title'),
            cell: (row) => (
                <ListTableActions>
                    <PreferencesListItemGroups preferenceId={row.id} />
                </ListTableActions>
            ),
        },
        {
            id: 'phrases',
            header: i18n.t('app.page.preference.phrase'),
            cell: (row) => (
                <ListTableActions>
                    <PreferencePhrasesCount preference={row} />
                </ListTableActions>
            ),
        },
        {
            id: 'actions',
            header: i18n.t('app.page.listView.actions'),
            className: 'list-table__td_actions',
            cell: (row) => (
                <ListTableActions>
                    <PreferencesListEditPreference preferenceId={row.id} />
                </ListTableActions>
            ),
        },
    ], [i18n]);

    return (
        <>
            <ListTable
                columns={columns}
                rows={items}
                rowKey={(row) => row.id}
                width={width}
                isRowSelected={(row) => preferencesStorage.getSelectedList().some((it) => it.id === row.id)}
                onRowClick={(row) => navigate(`/preference/item/${row.id}`)}
            />
            {preferencesStorage.getSelectedList().length ? (
                <PreferencesListSelectBlock itemsManager={preferencesStorage}>
                    <PreferencesListSelectAssignGroup />
                </PreferencesListSelectBlock>
            ) : null}
        </>
    );
};

export default PreferenceItemsTable;
